import { useMemo } from 'react';
import cn from 'classnames';

import { PauseIcon, PlayIcon, SpeakerIcon, SpeakerNoneIcon } from '../icons';
import { colors } from '../../colors';

import { useAudio } from './useAudio';
import * as css from './AudioPlayer.css';

type AudioPlayerProps = {
    audioSrc: string;
    className?: string;
};

export const AudioPlayer = ({ audioSrc, className }: AudioPlayerProps) => {
    const { audioRef, isPlaying, isMuted, progress, duration, currentTime, togglePlay, toggleMute, seek } =
        useAudio(audioSrc);

    const fillBackground = useMemo(() => {
        return `linear-gradient(
    to right,
    ${colors.teal[700]} ${progress}%,
    ${colors.teal[200]} ${progress}%
  )`;
    }, [progress]);

    return (
        <div className={cn(css.audioContainer, className)}>
            <audio ref={audioRef} src={audioSrc} />
            <button onClick={togglePlay}>
                {isPlaying ? (
                    <PauseIcon weight="fill" color={colors.teal[700]} size={15} />
                ) : (
                    <PlayIcon weight="fill" color={colors.teal[700]} size={15} />
                )}
            </button>
            <div>
                <span className={css.timeText}>{formatTime(currentTime)}</span>
                <span className={css.timeText} style={{ margin: '0 2px' }}>
                    /
                </span>
                <span className={css.timeText}>{formatTime(duration)}</span>
            </div>
            <input
                type="range"
                min="0"
                max={100}
                style={{
                    background: fillBackground,
                }}
                value={progress}
                onChange={(e) => seek(Number(e.target.value))}
                step="0.01"
                className={css.slider}
            />
            <button onClick={toggleMute}>
                {isMuted ? (
                    <SpeakerNoneIcon weight="fill" color={colors.teal[700]} />
                ) : (
                    <SpeakerIcon weight="fill" color={colors.teal[700]} />
                )}
            </button>
        </div>
    );
};

const formatTime = (time: number) => {
    if (time === Infinity) {
        return `00:99`;
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
        .toString()
        .padStart(2, '0');
    return `${minutes}:${seconds}`;
};
